import type { GetStaticProps } from "next";
import ImageBanner from "@components/ImageBanner";
import type { pageTitle, pageContent } from "@lib/types";
import { CarouselBasic } from "@components/Carousels";
import {
  getPageTitleContentFromDB,
  getPageContentFromDB,
} from "@lib/pagequerys";
import {
  Container,
  Heading,
  Section,
  List,
  Text,
  LinkComponent,
  Spacer,
} from "@components/SectionComponents";

// Gets static props from DB
export const getStaticProps: GetStaticProps = async () => {
  const pageIdDB: number = 6;

  // query DB for page title data
  const pageTitle: pageTitle = await getPageTitleContentFromDB(pageIdDB);

  // Array of pageContent - query DB for pageContent data
  const pageContent: pageContent[] = await getPageContentFromDB(pageIdDB);

  return {
    props: {
      pageTitle,
      pageContent,
    },
  };
};

// Home Page takes a pageTitle, and pageContent array
export default function Page({
  pageTitle,
  pageContent,
}: {
  pageTitle: pageTitle;
  pageContent: pageContent[];
}) {
  return (
    <>
      <ImageBanner title={pageTitle} />

      {/* Section 0 */}
      <Section id="cabins" colour="brown">
        <Container longer="bottom">
          <Heading h1={true} content={{ title: pageContent[0].title }} />
          <Text content={pageContent[0].text} />
          <List content={pageContent[0].list} />
          <Spacer type="medium" />
          <LinkComponent link1={pageContent[0].link1} />
        </Container>
      </Section>

      {/* Section 5 (out of order to not break the live deployed DB)  */}
      <Section id="red-kangaroo" colour="white">
        <CarouselBasic images={pageContent[5].carouselImages} overlap="top" />
        <Container longer="both">
          <Heading
            content={{
              title: pageContent[5].title,
              subtitle: pageContent[5].subtitle,
            }}
          />
          <Text content={pageContent[5].text} />
          <List content={pageContent[5].list} />
          <Spacer type="medium" />
          <LinkComponent link1={pageContent[5].link1} />
        </Container>
      </Section>

      {/* Section 1 */}
      <Section id="black-cockatoo" colour="cream">
        <CarouselBasic images={pageContent[1].carouselImages} overlap="top" />
        <Container longer="both">
          <Heading
            content={{
              title: pageContent[1].title,
              subtitle: pageContent[1].subtitle,
            }}
          />
          <Text content={pageContent[1].text} />
          <List content={pageContent[1].list} />
          <Spacer type="medium" />
          <LinkComponent link1={pageContent[1].link1} />
        </Container>
      </Section>

      {/* Section 7 (out of order to not break the live deployed DB)  */}
      <Section id="croc" colour="brown">
        <CarouselBasic images={pageContent[7].carouselImages} overlap="top" />
        <Container longer="both">
          <Heading
            content={{
              title: pageContent[7].title,
              subtitle: pageContent[7].subtitle,
            }}
          />
          <Text content={pageContent[7].text} />
          <List content={pageContent[7].list} />
          <Spacer type="medium" />
          <LinkComponent link1={pageContent[7].link1} />
        </Container>
      </Section>

      {/* Section 6 (out of order to not break the live deployed DB)  */}
      <Section id="emu" colour="white">
        <CarouselBasic images={pageContent[6].carouselImages} overlap="top" />
        <Container longer="top">
          <Heading
            content={{
              title: pageContent[6].title,
              subtitle: pageContent[6].subtitle,
            }}
          />
          <Text content={pageContent[6].text} />
          <List content={pageContent[6].list} />
          <Spacer type="medium" />
          <LinkComponent link1={pageContent[6].link1} />
        </Container>
      </Section>

      {/* Section 2 */}
      <Section id="pool-bar" colour="cream">
        <Container>
          <Heading content={{ title: pageContent[2].title }} />
          <Text content={pageContent[2].text} />
        </Container>
        <CarouselBasic
          images={pageContent[2].carouselImages}
          overlap="bottom"
        />
      </Section>

      {/* Section 3 */}
      <Section id="facilities" colour="brown">
        <Container longer="top">
          <Heading content={{ title: pageContent[3].title }} />
          <Text content={pageContent[3].text} />
          <List content={pageContent[3].list} />
        </Container>
      </Section>

      {/* Section 4 */}
      <Section colour="white">
        <Container>
          <Heading content={{ title: pageContent[4].title }} />
          <Text content={pageContent[4].text} />
          <LinkComponent link1={pageContent[4].link1} />
        </Container>
      </Section>
    </>
  );
}

//Meta Data
Page.title = "Accommodation";
Page.description =
  "The Crocodile Hunter Lodge offers unique, family-friendly rustic cabins, helping you reconnect with the natural world.";
